import React from 'react';
import { Typography, theme } from 'antd';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';

/**
 * Preloader — кит-прелоадер «кольцо» (WEB-739).
 * Канон: ../web2app-spec/design/shared-kit-mockup/src/kit/Preloader.tsx —
 * ратифицированная разметка/API повторены буква в букву (диаметр кольца,
 * паддинги, keyframes инжектятся локально через <style>, цвета из
 * theme.useToken()). Заменяет прямые antd <Spin> по всему кабинету и
 * публичным страницам.
 *
 * Публичные страницы (PublicQuiz/PublicPaywall) импортируют этот файл
 * НАПРЯМУЮ (`@/components/kit/Preloader`, default export), минуя барель
 * `kit/index.ts`, чтобы не тащить весь кит в публичный бандл.
 */

export interface PreloaderProps {
  /** Подпись под кольцом, напр. «Загружаем данные…» */
  label?: React.ReactNode;
  /** Компактные отступы (инлайн в карточках) */
  compact?: boolean;
  /**
   * Расширение канона: строчный вариант для чисто инлайновых мест (текст в
   * строке таблицы/попапа) — кольцо 14px, span-вёрстка, без блочных паддингов.
   */
  inline?: boolean;
  'data-testid'?: string;
}

export function Preloader({ label, compact, inline, ...rest }: PreloaderProps) {
  const common = useStrings(commonStrings);
  const { token } = theme.useToken();
  const testId = rest['data-testid'] ?? 'preloader';

  if (inline) {
    // расширение канона: inline-вариант для строчных мест
    return (
      <span
        role="status"
        aria-label={common.loading}
        data-testid={testId}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <style>{'@keyframes w2aSpin { to { transform: rotate(360deg); } }'}</style>
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: `2px solid ${token.colorFillSecondary}`,
            borderTopColor: token.colorPrimary,
            animation: 'w2aSpin 0.8s linear infinite',
            flexShrink: 0,
          }}
        />
        {label && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {label}
          </Typography.Text>
        )}
      </span>
    );
  }

  return (
    <div
      role="status"
      aria-label={common.loading}
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: compact ? '32px 16px' : '64px 24px',
      }}
    >
      <style>{'@keyframes w2aSpin { to { transform: rotate(360deg); } }'}</style>
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: `3px solid ${token.colorFillSecondary}`,
          borderTopColor: token.colorPrimary,
          animation: 'w2aSpin 0.8s linear infinite',
        }}
      />
      {label && (
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {label}
        </Typography.Text>
      )}
    </div>
  );
}

export default Preloader;
